import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { BookOpen, Check, ChevronLeft } from "lucide-react";
import api from "@/lib/api";
import ProgressBar from "@/components/ProgressBar";
import EmptyState from "@/components/EmptyState";

export default function TrackDetail() {
    const { slug } = useParams();
    const [track, setTrack] = useState(null);
    const [modules, setModules] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        Promise.all([
            api.get(`/tracks/${slug}`),
            api.get(`/modules?track=${encodeURIComponent(slug)}`),
        ])
            .then(([t, ms]) => {
                if (cancelled) return;
                setTrack(t.data);
                setModules(ms.data || []);
            })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [slug]);

    if (loading) return <div className="text-sm text-ink-500" data-testid="track-loading">Loading…</div>;
    if (!track) return <div className="text-sm text-ink-500">Track not found.</div>;

    return (
        <div className="space-y-10" data-testid={`track-detail-${slug}`}>
            <div>
                <Link to="/tracks" className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.15em] text-ink-500 hover:text-haevn-700 font-mono mb-4">
                    <ChevronLeft size={12} /> All tracks
                </Link>
                <div className="text-[10px] uppercase tracking-[0.25em] text-ink-400 font-mono mb-3">Curriculum Track</div>
                <h1 className="font-editorial text-4xl md:text-5xl text-ink-900 leading-[1.05]">{track.title}</h1>
                <p className="mt-4 text-base text-ink-500 max-w-2xl leading-relaxed">{track.description}</p>
            </div>

            {modules.length === 0 ? (
                <EmptyState
                    title="Content coming soon"
                    description={`Modules for ${track.title} will appear here once published.`}
                    icon={BookOpen}
                    testId="track-modules-empty"
                />
            ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-ink-300 border border-ink-300">
                    {modules.map((m, idx) => (
                        <Link
                            key={m.id}
                            to={`/modules/${m.id}`}
                            data-testid={`track-module-card-${m.id}`}
                            className="group bg-white p-8 hover:bg-ink-100/60 transition-colors flex flex-col"
                        >
                            <div className="flex items-start justify-between mb-6">
                                <div className="font-mono text-xs uppercase tracking-[0.2em] text-ink-400">
                                    {String(idx + 1).padStart(2, "0")}
                                </div>
                                <div className="flex items-center gap-2">
                                    {m.optional && (
                                        <div className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] font-mono text-ink-500 bg-ink-100 border border-ink-200 px-2 py-1">
                                            Optional
                                        </div>
                                    )}
                                    {(m.completed ?? ((m.progress_pct ?? 0) === 100)) && (
                                        <div className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] font-mono text-haevn-700 bg-haevn-50 border border-haevn-100 px-2 py-1">
                                            <Check size={10} strokeWidth={2} /> Complete
                                        </div>
                                    )}
                                </div>
                            </div>

                            <h3 className="font-heading text-xl text-ink-900 group-hover:text-haevn-700 transition-colors">{m.title}</h3>
                            {m.description && <p className="text-sm text-ink-500 mt-2 line-clamp-3">{m.description}</p>}

                            <div className="mt-auto pt-6">
                                <ProgressBar value={m.progress_pct ?? 0} size="sm" />
                                <div className="mt-2 flex items-center justify-between text-[11px] font-mono uppercase tracking-[0.15em] text-ink-400">
                                    <span>{m.completed_lessons ?? 0}/{m.lesson_count ?? 0} lessons</span>
                                    <span>{m.estimated_minutes ?? 30} min</span>
                                </div>
                            </div>
                        </Link>
                    ))}
                </div>
            )}
        </div>
    );
}
